// @ts-nocheck
import EditIcon from "@insite/mobius/Icons/Edit2";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { DataTableProps } from "@insite/mobius/DataTable/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { Hidden } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import { CustomerPoNumber } from "@store/Framework/Services/CustomerPoNumberService";
import FadeIn from "@store/Library/Components/Reporting/Common/FadeIn";
import ManageDefaultPOCard from "@store/Library/Widgets/ManageDefaultPO/ManageDefaultPOCard";
import ManageDefaultPOEditingCard from "@store/Library/Widgets/ManageDefaultPO/ManageDefaultPOEditingCard";
import ManageDefaultPOTable from "@store/Library/Widgets/ManageDefaultPO/ManageDefaultPOTable";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import Close from "@toolkit/Library/Components/Icons/Close";
import React from "react";
import { css } from "styled-components";

export interface ManageDefaultPOTableStyles {
    dataTable?: DataTableProps;
    removePoButton?: IconPresentationProps;
    editPoButton?: IconPresentationProps;
    buttonCell?: DataTableCellProps;
    floatingTagWrapper?: InjectableCss;

    cardSectionContainer?: InjectableCss;
}

export const manageDefaultPOTableStyles: ManageDefaultPOTableStyles = {
    cardSectionContainer: {
        css: css`
            display: flex;
            padding: 0;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            gap: 2.5rem;
        `,
    },
    buttonCell: {
        css: css`
            padding-inline: 0;
            display: flex;
            align-items: center;
            justify-content: space-evenly;
        `,
    },

    removePoButton: {
        src: Close,
        size: 27,
        css: css`
            color: ${getColor("primary")};
            :hover {
                color: ${getColor("secondary")};
            }
        `,
    },

    editPoButton: {
        src: EditIcon,
        size: 27,
        css: css`
            color: ${getColor("secondary")};
            :hover {
                color: ${getColor("primary")};
            }
        `,
    },
};

interface ManageDefaultPOTableProps {
    customerPOs: CustomerPoNumber[];
    extendedStyles?: ManageDefaultPOTableStyles;
    overrideStyles?: ManageDefaultPOTableStyles;
    tableCellWidthOverrides?: number[];
    editingPo?: CustomerPoNumber;
    setEditingPo: (customerPoNumber: CustomerPoNumber | undefined) => void;
    onSave: (po: CustomerPoNumber | null) => Promise<void>;
    onDelete: (poId: string) => void;
    validateEndDate: (startDate: Date, endDate: Date | null) => boolean;
    validateStartDate: (startDate: Date, endDate: Date | null) => boolean;
    activePoId?: string;
    isNew?: boolean;
}

const ManageDefaultPODisplay: React.FC<ManageDefaultPOTableProps> = ({
    customerPOs,
    extendedStyles,
    overrideStyles,
    tableCellWidthOverrides,
    editingPo,
    setEditingPo,
    onSave,
    onDelete,
    validateEndDate,
    validateStartDate,
    activePoId,
    isNew = false,
}) => {
    const styles = useExpandProps(manageDefaultPOTableStyles, extendedStyles, overrideStyles);

    return (
        <>
            <Hidden below="md">
                <ManageDefaultPOTable
                    customerPOs={customerPOs}
                    isNew={isNew}
                    editingPo={editingPo}
                    setEditingPo={setEditingPo}
                    onSave={onSave}
                    onDelete={onDelete}
                    validateEndDate={validateEndDate}
                    validateStartDate={validateStartDate}
                    activePoId={activePoId}
                ></ManageDefaultPOTable>
            </Hidden>
            <Hidden
                css={css`
                    width: 100%;
                `}
                above="sm"
            >
                <StyledWrapper {...styles.cardSectionContainer}>
                    {customerPOs.map((customerPoNumber, index) => {
                        const isActiveEditingPo = editingPo?.id === customerPoNumber.id;
                        return isActiveEditingPo ? (
                            <FadeIn duration={250} key={`${customerPoNumber.id}editing`} animate appear>
                                <ManageDefaultPOEditingCard
                                    onSave={onSave}
                                    isNew={isNew}
                                    onCancel={() => onSave(null)}
                                    customerPoNumber={customerPoNumber}
                                    validateEndDate={validateEndDate}
                                    validateStartDate={validateStartDate}
                                />
                            </FadeIn>
                        ) : (
                            <FadeIn animate key={customerPoNumber.id} duration={250} appear>
                                <ManageDefaultPOCard
                                    isActive={activePoId === customerPoNumber.id}
                                    onDelete={onDelete}
                                    isEditing={!!editingPo}
                                    customerPoNumber={customerPoNumber}
                                    setEditingPo={setEditingPo}
                                />
                            </FadeIn>
                        );
                    })}
                </StyledWrapper>
            </Hidden>
        </>
    );
};

export default ManageDefaultPODisplay;
